<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();
useHead({
  titleTemplate: () => t("seo.layouts./viber-business-messages-custom-auto-reply.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-business-messages-custom-auto-reply.description") },
    {
      property: "og:title", content: () => t("seo.layouts./viber-business-messages-custom-auto-reply.title")
    },
    { property: "og:description", content: () => t("seo.layouts./viber-business-messages-custom-auto-reply.description") },
    {
      property: "og:image",
      content:
        "https://cdn-web.smsbat.com/uploads/2024/viber_bm.png",
    },
    { property: "og:image:width", content: "972" },
    { property: "og:image:height", content: "972" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
});

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})

</script>

<template>
  <div class="viber-business-messages-custom-auto-reply">
    <div class="main-banner">
      <div class="container">
        <div class="banner-content">
          <div class="banner-text">
            <h1 class="banner-title">
              {{ $t('customAutoReply.title') }}
            </h1>
            <p class="banner-description">
              {{ $t('customAutoReply.subtitle') }}
            </p>
          </div>
          <div class="banner-image-container">
            <NuxtImg src="/images/viber-custom-auto-reply.png" class="mt-20 w-full h-auto object-contain" alt="Viber Custom Auto Reply" :title="`${$t('customAutoReply.subtitle')} ${$t('global.image')}`" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" />
          </div>
        </div>
      </div>
    </div>

    <!-- Global Stats Section -->
    <div class="page-section !pt-0">
      <div class="section-content">
        <div class="stats-container">
          <div class="stats-title">
            {{ $t('customAutoReply.stats.title') }}
          </div>
          <div class="stats-subtitle">
            {{ $t('customAutoReply.stats.subtitle') }}
          </div>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">1000</div>
              <div class="stat-label">{{ $t('customAutoReply.stats.characters') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">24/7</div>
              <div class="stat-label">{{ $t('customAutoReply.stats.availability') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">0€</div>
              <div class="stat-label">{{ $t('customAutoReply.stats.cost') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">100%</div>
              <div class="stat-label">{{ $t('customAutoReply.stats.customizable') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="section-content">
        <div class="section-title">
          {{ $t('customAutoReply.features.title') }}
        </div>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#3070b6" stroke-width="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#3070b6"></circle>
                <path d="M21 15L16 10L5 21" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('customAutoReply.features.customizable.title') }}</h3>
              <p>{{ $t('customAutoReply.features.customizable.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 17L12 22L22 17" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 12L12 17L22 12" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('customAutoReply.features.realtime.title') }}</h3>
              <p>{{ $t('customAutoReply.features.realtime.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <circle cx="12" cy="12" r="10" stroke="#3070b6" stroke-width="2"></circle>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('customAutoReply.features.free.title') }}</h3>
              <p>{{ $t('customAutoReply.features.free.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('customAutoReply.features.richContent.title') }}</h3>
              <p>{{ $t('customAutoReply.features.richContent.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 17L12 22L22 17" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 12L12 17L22 12" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('customAutoReply.features.integration.title') }}</h3>
              <p>{{ $t('customAutoReply.features.integration.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('customAutoReply.features.instant.title') }}</h3>
              <p>{{ $t('customAutoReply.features.instant.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="page-section">
      <div class="section-content">
        <div class="section-title">
          {{ $t('customAutoReply.howItWorks.title') }}
        </div>
        <div class="how-it-works-content">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>{{ $t('customAutoReply.howItWorks.step1.title') }}</h3>
              <p>{{ $t('customAutoReply.howItWorks.step1.description') }}</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>{{ $t('customAutoReply.howItWorks.step2.title') }}</h3>
              <p>{{ $t('customAutoReply.howItWorks.step2.description') }}</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>{{ $t('customAutoReply.howItWorks.step3.title') }}</h3>
              <p>{{ $t('customAutoReply.howItWorks.step3.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Viber List Messages Integration Section -->
    <div class="page-section bg-gray-50">
      <div class="section-content">
        <div class="section-title">
          {{ $t('customAutoReply.integration.title') }}
        </div>
        <div class="integration-content">
          <div class="integration-text">
            <h3>{{ $t('customAutoReply.integration.subtitle') }}</h3>
            <p>{{ $t('customAutoReply.integration.description') }}</p>
            <div class="integration-benefits">
              <div class="benefit-item">
                <div class="benefit-icon">✓</div>
                <div class="benefit-text">{{ $t('customAutoReply.integration.benefit1') }}</div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">✓</div>
                <div class="benefit-text">{{ $t('customAutoReply.integration.benefit2') }}</div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">✓</div>
                <div class="benefit-text">{{ $t('customAutoReply.integration.benefit3') }}</div>
              </div>
            </div>
          </div>
          <div class="integration-image">
            <NuxtImg src="/images/viber_survey.png" width="800" height="600" alt="Viber List Messages Integration" format="webp" loading="lazy" class="w-full h-auto object-contain" />
          </div>
        </div>
      </div>
    </div>

    <!-- Benefits Section -->
    <div class="page-section">
      <div class="section-content">
        <div class="section-title">
          {{ $t('customAutoReply.benefits.title') }}
        </div>
        <div class="benefits-list">
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('customAutoReply.benefits.provideInfo') }}</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('customAutoReply.benefits.alternativeContact') }}</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('customAutoReply.benefits.improveExperience') }}</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('customAutoReply.benefits.minimizeDissatisfaction') }}</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('customAutoReply.benefits.toggleOnOff') }}</div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">✓</div>
            <div class="benefit-text">{{ $t('customAutoReply.benefits.personalizedApproach') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('customAutoReply.cta.title') }}</div>
          <div class="section-head-description text-gray-600">{{ $t('customAutoReply.cta.description') }}</div>
        </div>
        <div class="section-content">
          <div class="banner-action flex flex-wrap items-center justify-center gap-4">
            <NuxtLink to="https://omni.smsbat.com/register-ca" external target="_blank" class="nav-link button filled">
              <p>{{ $t('links.registerBusinessAccount') }}</p>
            </NuxtLink>
            <NuxtLink to="https://help.smsbat.com/uk/hc/1780048863/1/api?category_id=3#viber_survey" external
              target="_blank" class="button outlined">
              <p>{{ $t('customAutoReply.cta.viewDocs') }}</p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
.main-banner {
  padding: 80px 0 80px 0;
  min-height: 80vh;
  display: flex;
  align-items: center;
  width: 100%;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.banner-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.banner-text {
  flex: 1;
}

.banner-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.banner-description {
  font-size: 1.2rem;
  opacity: 0.9;
  line-height: 1.6;
}

.banner-image-container {
  flex: 1;
  text-align: center;
}

.page-section {
  padding: 80px 0;
}

.section-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 60px;
  color: #333;
}

.stats-container {
  text-align: center;
  background: #f8f9fa;
  padding: 60px 40px;
  border-radius: 20px;
}

.stats-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

.stats-subtitle {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 40px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 3rem;
  font-weight: bold;
  color: #3070b6;
  margin-bottom: 10px;
}

.stat-label {
  font-size: 1rem;
  color: #666;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
}

.feature-icon {
  flex-shrink: 0;
}

.feature-content h3 {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.feature-content p {
  color: #666;
  line-height: 1.6;
}

.how-it-works-content {
  max-width: 800px;
  margin: 0 auto;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 40px;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.step-number {
  width: 60px;
  height: 60px;
  background: #3070b6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content h3 {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.step-content p {
  color: #666;
  line-height: 1.6;
}

.benefits-list {
  max-width: 800px;
  margin: 0 auto;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
}

.benefit-icon {
  width: 30px;
  height: 30px;
  background: #3070b6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.benefit-text {
  font-size: 1.1rem;
  color: #333;
}

.nav-link {
  text-decoration: none;
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.integration-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.integration-text h3 {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

.integration-text p {
  font-size: 1.1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 30px;
}

.integration-benefits {
  margin-top: 30px;
}

.integration-image {
  text-align: center;
}

.integration-image img {
  max-width: 100%;
  height: auto;
}

@media (max-width: 768px) {
  .banner-content {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 0 15px;
  }

  .banner-title {
    font-size: 2rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .section-title {
    font-size: 2rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .step-item {
    flex-direction: column;
    text-align: center;
  }

  .integration-content {
    grid-template-columns: 1fr;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 10px;
  }

  .banner-content {
    padding: 0 10px;
  }

  .banner-title {
    font-size: 1.6rem;
  }

  .banner-description {
    font-size: 1rem;
  }
}

.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;
}

.bg-gradient .section-head-title {
  color: white;
}

.bg-gradient .text-gray-600 {
  color: #111827;
}

.bottom-banner {
  padding: 80px 0;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 52px;
  font-size: 18px;
  font-weight: 800;
  border: 2px solid #3070b6;
  border-radius: 10px;
  transition: 0.2s ease;
  text-decoration: none;
  cursor: pointer;
}

.button.filled {
  color: #fff;
  background: #3070b6;
}

.button.filled:hover {
  color: #3070b6;
  background: #fff;
}

.button.outlined {
  color: #3070b6;
  background: #fff;
}

.button.outlined:hover {
  color: #fff;
  background: #3070b6;
}

@media (max-width: 768px) {
  .main-banner {
    .banner-title {
      margin: 0 auto;
      text-align: center;
    }

    .banner-description {
      margin: 0 auto;
      text-align: center;
    }
  }
}

@media (max-width: 992px) {
  .main-banner {
    padding: 0 0 80px 0;
  }

  .viber-business-messages-custom-auto-reply {
    padding-top: 0;
  }
}
</style>
